// @ds/data/subQueriesContext — the sub-query context object + consumer hooks.
//
// Split out of `SubQueriesProvider.tsx` so that module exports components only.

import { createContext, useContext } from "react";

export interface SubQueriesContextValue {
  /**
   * Get rows for a sub-query key + record id.
   *
   * Returns `{ rows: [], isLoading: false }` when the key is not registered.
   * Shape is identical to `useSubQuery` so consumers using the context value
   * directly (`useContext(SubQueriesContext)`) get the same contract as those
   * going through the hook. Prefer the hook for component code.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: <S = any>(key: string, recordId: number) => { rows: S[]; isLoading: boolean };
  /** True while any of the registered sub-queries is still loading. */
  isLoading: boolean;
}

export const SubQueriesContext = createContext<SubQueriesContextValue | null>(null);


/**
 * Read sub-query rows for a record. Must be called inside a
 * `<SubQueriesProvider>`. After the F-1 fix every table chassis
 * (DataTable / SectionTable / PickerTable / EntityViewPage) mounts a
 * provider — so this never legitimately fires without one.
 *
 * Dev guard: throws when called outside a provider, surfacing config
 * errors at the first render rather than via silent empty arrays. Prod
 * gracefully returns `{rows: [], isLoading: false}` so a misconfigured
 * cell doesn't crash the page.
 *
 * Prefer `useSubQueryOptional` when the widget is genuinely allowed to
 * render with no provider (e.g. a standalone showcase / detached preview).
 */
export function useSubQuery<S>(
  key: string,
  recordId: number,
): { rows: S[]; isLoading: boolean } {
  const ctx = useContext(SubQueriesContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error(
        `useSubQuery("${key}", ${recordId}) called outside a <SubQueriesProvider>. ` +
          `Mount one at the boundary of the surface rendering this widget.`,
      );
    }
    return { rows: [] as S[], isLoading: false };
  }
  return ctx.get<S>(key, recordId);
}

/**
 * Same as `useSubQuery` but soft-fails when no provider is mounted —
 * returns `{rows: [], isLoading: false}` instead of throwing. Use for
 * genuinely-detached widgets (showcases, dev previews, components that
 * may or may not be wrapped depending on where they're rendered).
 */
export function useSubQueryOptional<S>(
  key: string,
  recordId: number,
): { rows: S[]; isLoading: boolean } {
  const ctx = useContext(SubQueriesContext);
  if (!ctx) return { rows: [] as S[], isLoading: false };
  return ctx.get<S>(key, recordId);
}
